import { Dispatch, RefObject, SetStateAction, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useFormContext } from 'react-hook-form';
import { useSnackBar } from '../../../hooks/useSnackBar';
import { useTranslation } from '../../../hooks/useTranslation';
import type { AgentFormData } from '../../../interfaces/Agent';
import { KnowledgeFileData } from '../../../interfaces/Knowledge';
import type { TranslationKey } from '../../../types/i18n';
import {
  acceptAssistantEmployeeDirectory,
  acceptAssistantKnowledgeFiles,
  addKnowledgeFiles,
  KnowledgeFileRejectionReason,
  MAX_KNOWLEDGE_FILES,
  RejectedKnowledgeFile,
} from './acceptAssistantFiles';
import { listenForAgentFiles } from './agentFileMessaging';

const KNOWLEDGE_REJECTION_MESSAGES: Record<
  KnowledgeFileRejectionReason,
  TranslationKey
> = {
  type: '"{{name}}" wasn\'t added: only PDF or CSV files are accepted.',
  size: '"{{name}}" wasn\'t added: files can be up to 20 MB.',
  duplicate:
    '"{{name}}" wasn\'t added: a file with this name is already attached.',
  limit: '"{{name}}" wasn\'t added: up to {{max}} files can be attached.',
  read: '"{{name}}" wasn\'t added: the file couldn\'t be read.',
};

interface UseAssistantFileAttachmentsOptions {
  iframeRef: RefObject<HTMLIFrameElement>;
  expectedOrigin: string;
  agentType: 'call' | 'chat';
  // From useKnowledgeFile, the same state the Knowledge step adds files to
  knowledgeFilesData: KnowledgeFileData[];
  setKnowledgeFilesData: Dispatch<SetStateAction<KnowledgeFileData[]>>;
}

/**
 * Holds files attached in the agent-creation assistant exactly as if they'd
 * been added in the Knowledge or Capabilities step, so the existing create
 * submit uploads them once the agent exists. Must be used inside the agent
 * form's FormProvider.
 */
const useAssistantFileAttachments = ({
  iframeRef,
  expectedOrigin,
  agentType,
  knowledgeFilesData,
  setKnowledgeFilesData,
}: UseAssistantFileAttachmentsOptions): void => {
  const { setValue } = useFormContext<AgentFormData>();
  const displayMessage = useSnackBar();
  const { t } = useTranslation();

  // Read at message time, so the listener needn't re-subscribe on every add
  const knowledgeFilesRef = useRef(knowledgeFilesData);
  knowledgeFilesRef.current = knowledgeFilesData;
  const latest = useRef({ setValue, displayMessage, t, setKnowledgeFilesData });
  latest.current = { setValue, displayMessage, t, setKnowledgeFilesData };

  useEffect(() => {
    const onKnowledgeFiles = async (files: File[]) => {
      const { displayMessage, t, setKnowledgeFilesData } = latest.current;
      const { accepted, rejected } = await acceptAssistantKnowledgeFiles(
        files,
        knowledgeFilesRef.current
      );
      // Another batch may have landed while this one was encoding, so check
      // again against the latest list. flushSync runs the updater now, which
      // lets its rejections join the message below.
      let rejectedOnAdd: RejectedKnowledgeFile[] = [];
      if (accepted.length > 0) {
        flushSync(() => {
          setKnowledgeFilesData(previous => {
            const result = addKnowledgeFiles(previous, accepted);
            rejectedOnAdd = result.rejected;
            return result.files;
          });
        });
      }
      const allRejected = [...rejected, ...rejectedOnAdd];
      if (allRejected.length > 0) {
        displayMessage(
          allRejected
            .map(({ name, reason }) =>
              t(KNOWLEDGE_REJECTION_MESSAGES[reason], {
                name,
                max: MAX_KNOWLEDGE_FILES,
              })
            )
            .join(' '),
          'error',
          6000
        );
      }
    };

    const onEmployeeDirectory = async (file: File) => {
      const { setValue, displayMessage, t } = latest.current;
      const result = await acceptAssistantEmployeeDirectory(file);
      if (!result.ok) {
        displayMessage(t(result.error), 'error', 6000);
        return;
      }
      setValue('transfer_calls', 'employee_directory', { shouldDirty: true });
      setValue('employee_directory_staged_file', result.stagedFile);
    };

    return listenForAgentFiles({
      expectedOrigin,
      expectedSource: iframeRef.current?.contentWindow ?? null,
      agentType,
      onFiles: (kind, files) => {
        if (kind === 'knowledge') void onKnowledgeFiles(files);
        else if (files[0]) void onEmployeeDirectory(files[0]);
      },
    });
  }, [iframeRef, expectedOrigin, agentType]);
};

export default useAssistantFileAttachments;
